/*
 * Shared builder for sandboxed <iframe srcdoc> content — the single place the
 * security policy lives. The frame runs with sandbox="allow-scripts" WITHOUT
 * allow-same-origin (opaque origin: it can execute author JS but cannot touch
 * the host page), constrained by a strict CSP that only reaches the vetted
 * static CDNs (cdnjs / jsDelivr) — so a chart can pull Chart.js / D3 but has no
 * exfiltration surface. Used by the web WidgetFrame and by the server-side
 * assignment-PDF chart embeds, which must stay byte-for-byte consistent.
 */

export const SANDBOXED_EMBED_CSP =
  "default-src 'none'; " +
  "script-src 'unsafe-inline' https://cdnjs.cloudflare.com; " +
  "style-src 'unsafe-inline' https://cdnjs.cloudflare.com; " +
  'img-src data: https://cdnjs.cloudflare.com https://*.tile.openstreetmap.org https://*.basemaps.cartocdn.com; ' +
  'font-src data: https://cdnjs.cloudflare.com; ' +
  'connect-src https://cdnjs.cloudflare.com https://cdn.jsdelivr.net';

/** postMessage type an auto-height embed sends its content height with. */
export const EMBED_HEIGHT_MESSAGE = 'naia:embed_height';

/** Bridge that lets an interactive widget post a result up to the host. */
export function widgetBridgeScript(toolCallId: string): string {
  const id = JSON.stringify(toolCallId);
  return (
    '<script>(function(){window.Naia={submit:function(v){' +
    `try{parent.postMessage({type:"naia:widget_result",toolCallId:${id},value:v},"*");}catch(e){}` +
    '}};window.Naïa=window.Naia;})();</script>'
  );
}

/*
 * Measure script: reports the content's full height to the parent on every
 * change (ResizeObserver) plus a few delayed samples, so charts whose SVG/canvas
 * lays out asynchronously (after the CDN lib loads) are captured at their final
 * size rather than an early, too-small one.
 */
function autoHeightScript(embedId: string): string {
  const id = JSON.stringify(embedId);
  return (
    '<script>(function(){var post=function(){parent.postMessage({type:' +
    JSON.stringify(EMBED_HEIGHT_MESSAGE) +
    `,id:${id},height:Math.max(document.documentElement.scrollHeight,document.body.scrollHeight)},"*");};` +
    'if(window.ResizeObserver)new ResizeObserver(post).observe(document.documentElement);' +
    'window.addEventListener("load",post);' +
    '[0,150,400,800,1500].forEach(function(ms){setTimeout(post,ms);});})();</script>'
  );
}

export interface SandboxedSrcDocOptions {
  /** Author HTML placed in <body>. */
  readonly content: string;
  /** CSS for html/body (framing). */
  readonly bodyCss: string;
  /** Interactive-widget bridge, keyed by toolCallId. */
  readonly bridgeToolCallId?: string;
  /** When set, the frame reports its height to the parent under this id. */
  readonly autoHeightId?: string;
}

/** Build the full srcdoc document for a sandboxed embed. */
export function buildSandboxedSrcDoc(opts: SandboxedSrcDocOptions): string {
  const head = [
    opts.bridgeToolCallId ? widgetBridgeScript(opts.bridgeToolCallId) : '',
    opts.autoHeightId ? autoHeightScript(opts.autoHeightId) : '',
  ].join('');
  return (
    '<!doctype html><html><head><meta charset="utf-8">' +
    '<meta name="viewport" content="width=device-width,initial-scale=1">' +
    `<meta http-equiv="Content-Security-Policy" content="${SANDBOXED_EMBED_CSP}">` +
    `<style>${opts.bodyCss}</style>${head}</head><body>${opts.content}</body></html>`
  );
}
